import { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LayoutDashboard, ShieldCheck, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      toast.success('Password reset. Please sign in.');
      navigate('/login');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Reset link is invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      {/* Left - Brand */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: '#0A3622' }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-16">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-white">SnuggleMind</span>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white leading-tight mb-5">
            Choose a new<br />
            <span style={{ color: '#D98A4A' }}>password.</span>
          </h2>
          <p
            className="text-[15px] font-body leading-relaxed max-w-sm"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Use at least 8 characters. Mix upper and lowercase letters, numbers, and a symbol
            for stronger protection.
          </p>
        </div>
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.08]"
          style={{ background: 'radial-gradient(circle, #D98A4A 0%, transparent 70%)' }}
        />
      </div>

      {/* Right - Form */}
      <div
        className="w-full lg:w-[55%] flex flex-col justify-center px-8 lg:px-16 xl:px-20"
        style={{ background: '#F7F6F2' }}
      >
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2.5 mb-10 lg:hidden">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: '#0A3622' }}
            >
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold" style={{ color: '#1A1A1A' }}>
              SnuggleMind
            </span>
          </div>

          {tokenMissing ? (
            <div className="text-center" data-testid="reset-password-invalid">
              <div
                className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-5"
                style={{ background: 'rgba(200,75,49,0.12)' }}
              >
                <AlertCircle size={26} style={{ color: '#C84B31' }} />
              </div>
              <h1
                className="font-heading text-3xl font-bold tracking-tight mb-2"
                style={{ color: '#1A1A1A' }}
              >
                Invalid reset link
              </h1>
              <p
                className="font-body text-[14px] leading-relaxed mb-8"
                style={{ color: '#4B5563' }}
              >
                This link is missing its token. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center h-11 px-6 rounded-lg font-body font-medium text-white text-[14px]"
                style={{ background: '#0A3622' }}
                data-testid="reset-password-request-new"
              >
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={18} style={{ color: '#2D7A5D' }} />
                <span
                  className="text-[11px] uppercase tracking-[0.22em] font-semibold"
                  style={{ color: '#2D7A5D' }}
                >
                  Verified reset link
                </span>
              </div>
              <h1
                className="font-heading text-3xl font-bold tracking-tight mb-1.5"
                style={{ color: '#1A1A1A' }}
              >
                Set a new password
              </h1>
              <p className="font-body text-[15px] mb-7" style={{ color: '#4B5563' }}>
                Choose something strong you haven't used before.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="new-password"
                    className="text-[12px] font-body font-medium"
                    style={{ color: '#4B5563' }}
                  >
                    New password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    data-testid="reset-password-new-input"
                    className="h-11 rounded-lg font-body text-[14px]"
                    style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label
                    htmlFor="confirm-password"
                    className="text-[12px] font-body font-medium"
                    style={{ color: '#4B5563' }}
                  >
                    Confirm new password
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    minLength={8}
                    data-testid="reset-password-confirm-input"
                    className="h-11 rounded-lg font-body text-[14px]"
                    style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-password-submit-button"
                  className="w-full h-11 rounded-lg font-body font-medium text-white text-[14px]"
                  style={{ background: '#0A3622' }}
                >
                  {loading ? 'Resetting…' : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
